// DS EDIT 4 — "Discuss" button for the Document Viewer toolbar.
import { useState } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { discussDocumentService } from "@/services/discussDocumentService";

interface DiscussDocumentButtonProps {
  itemId: string;
  itemType: "document" | "source";
  title: string | null | undefined;
  contentHtml: string | null | undefined;
}

export function DiscussDocumentButton({
  itemId,
  itemType,
  title,
  contentHtml,
}: DiscussDocumentButtonProps) {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspaceId } = useWorkspace();
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { threadId, aiOpening } = await discussDocumentService.startDiscussion({
        itemId,
        itemType,
        title,
        contentHtml,
        workspaceId: currentWorkspaceId,
      });
      queryClient.invalidateQueries({ queryKey: ["chat-thread-summaries"] });
      queryClient.invalidateQueries({ queryKey: ["/api/chat-threads"] });
      if (!aiOpening) {
        toast({
          title: "Discussion ready",
          description: "Ask me anything about this document.",
        });
      }
      navigate(`/chat?threadId=${threadId}`);
    } catch {
      toast({
        title: "Couldn't start discussion",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleClick}
      disabled={busy}
      title="Start a chat about this document"
      data-testid="button-discuss-document"
    >
      {busy ? (
        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
      ) : (
        <MessageSquare className="h-4 w-4 mr-1" />
      )}
      {busy ? "Starting..." : "Discuss"}
    </Button>
  );
}
